import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAiCache, AI_TTL } from '../contexts/AiCacheContext';
import {
  Users, AlertTriangle, Clock, TrendingUp, TrendingDown, Minus,
  Zap, Activity, ChevronRight, RefreshCw
} from 'lucide-react';

/**
 * PopulationView — Doctor's full patient panel as a risk-sorted grid.
 * Shows every assigned patient: risk score, discharge day, silence status, conditions.
 */
const PopulationView = () => {
  const navigate = useNavigate();
  const { getOrFetch } = useAiCache();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('risk'); // risk | discharge | silence
  const [filterWindow, setFilterWindow] = useState('ALL'); // ALL | 7d | 14d | stable
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/risk-assessments/pending?minScore=0&limit=50');
      const raw = res.data?.data || [];
      setPatients(raw);

      // Fetch AI population insight (non-blocking)
      if (raw.length > 0) {
        setAiLoading(true);
        fetchAiInsight().finally(() => setAiLoading(false));
      }
    } catch {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAiInsight = () =>
    getOrFetch('population-insight', () => api.get('/ai/population-insight').then(r => r.data.insight || null), AI_TTL.POPULATION_INSIGHT)
      .then(insight => { if (insight) setAiInsight(insight); })
      .catch(() => {});

  const processed = patients.map(p => {
    const assessment = p.latestAssessment || {};
    const snapshot = assessment.patientSnapshot || {};
    const riskScore = p.riskScore?.overall ?? assessment.scores?.overall ?? 0;
    const trend = p.riskScore?.trend || assessment.trendAnalysis?.direction || 'stable';
    const daysSinceDischarge = snapshot.daysSinceDischarge ?? null;
    const lastCheckin = p.patient?.lastActiveAt ? new Date(p.patient.lastActiveAt) : null;
    const hoursSilent = lastCheckin ? Math.floor((Date.now() - lastCheckin) / 3600000) : null;
    const isSilent = hoursSilent != null && hoursSilent >= 48;
    const inSevenDayWindow = daysSinceDischarge != null && daysSinceDischarge <= 7;
    const inFourteenDayWindow = daysSinceDischarge != null && daysSinceDischarge <= 14;
    const conditions = snapshot.activeConditions || [];
    return { ...p, riskScore, trend, daysSinceDischarge, hoursSilent, isSilent, inSevenDayWindow, inFourteenDayWindow, conditions };
  });

  const filtered = processed.filter(p => {
    if (filterWindow === '7d') return p.inSevenDayWindow;
    if (filterWindow === '14d') return p.inFourteenDayWindow;
    if (filterWindow === 'silent') return p.isSilent;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'risk') return b.riskScore - a.riskScore;
    if (sortBy === 'discharge') return (a.daysSinceDischarge ?? 999) - (b.daysSinceDischarge ?? 999);
    if (sortBy === 'silence') return (b.hoursSilent ?? 0) - (a.hoursSilent ?? 0);
    return 0;
  });

  const stats = {
    total: processed.length,
    critical: processed.filter(p => p.riskScore >= 70).length,
    inWindow: processed.filter(p => p.inSevenDayWindow).length,
    silent: processed.filter(p => p.isSilent).length,
  };

  const getRiskStyle = (score) => {
    if (score >= 70) return { bar: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200', border: 'border-l-red-500' };
    if (score >= 45) return { bar: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200', border: 'border-l-amber-500' };
    return { bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', border: 'border-l-emerald-500' };
  };

  const TrendIcon = (trend) => trend === 'worsening' || trend === 'up' ? TrendingUp
    : trend === 'improving' || trend === 'down' ? TrendingDown : Minus;

  if (loading) return (
    <div className="space-y-4 py-6">
      <div className="h-8 bg-slate-200 rounded w-1/3 animate-pulse" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[1,2,3,4].map(i => <div key={i} className="h-20 bg-slate-200 rounded-2xl animate-pulse" />)}
      </div>
      <div className="space-y-2">
        {[1,2,3,4,5].map(i => <div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Patient Panel</h2>
          <p className="text-slate-500 text-sm mt-1">{stats.total} patients under monitoring</p>
        </div>
        <button onClick={load} className="p-2 rounded-xl hover:bg-slate-100 transition-all">
          <RefreshCw className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Patients', value: stats.total, icon: Users, color: 'text-slate-600' },
          { label: 'Critical (70+)', value: stats.critical, icon: AlertTriangle, color: 'text-red-600' },
          { label: 'In 7-day Window', value: stats.inWindow, icon: Clock, color: 'text-amber-600' },
          { label: 'Silent 48h+', value: stats.silent, icon: Activity, color: 'text-orange-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-4 border border-outline-variant/10 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{label}</p>
              <Icon className={`w-4 h-4 ${color}`} />
            </div>
            <p className={`text-3xl font-black tracking-tighter ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* AI Population Insight */}
      {(aiLoading || aiInsight) && (
        <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-2xl">
          <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Zap className="w-3 h-3" /> AI Panel Insight
          </p>
          {aiLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
              <span className="text-xs text-blue-400">Analyzing your patient panel…</span>
            </div>
          ) : (
            <p className="text-sm text-slate-700 leading-relaxed">{aiInsight}</p>
          )}
        </div>
      )}

      {/* Filters + Sort */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'ALL', label: 'All' },
            { value: '7d', label: '≤7 days post-discharge' },
            { value: '14d', label: '≤14 days post-discharge' },
            { value: 'silent', label: 'Silent 48h+' },
          ].map(({ value, label }) => (
            <button key={value} onClick={() => setFilterWindow(value)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                filterWindow === value ? 'bg-primary text-white border-primary' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
              }`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="font-bold">Sort:</span>
          {[['risk', 'Risk'], ['discharge', 'Discharge'], ['silence', 'Silence']].map(([val, label]) => (
            <button key={val} onClick={() => setSortBy(val)}
              className={`px-2.5 py-1 rounded-lg font-bold transition-all ${sortBy === val ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Patient Grid */}
      {sorted.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
          <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-bold text-slate-900">No patients match this filter</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((p, i) => {
            const style = getRiskStyle(p.riskScore);
            const T = TrendIcon(p.trend);
            const trendColor = p.trend === 'worsening' ? 'text-red-500' : p.trend === 'improving' ? 'text-emerald-500' : 'text-slate-400';
            return (
              <button key={p.patient?._id || i} onClick={() => navigate(`/patient/${p.patient?._id}`)}
                className="w-full bg-white rounded-2xl border border-outline-variant/10 p-4 flex items-center gap-4 hover:shadow-md transition-all text-left">
                {/* Risk score */}
                <div className="shrink-0 w-14 text-center">
                  <div className={`text-2xl font-black tracking-tighter ${p.riskScore >= 70 ? 'text-red-600' : p.riskScore >= 45 ? 'text-amber-600' : 'text-emerald-600'}`}>{p.riskScore}</div>
                  <div className="flex justify-center mt-1">
                    <T className={`w-3.5 h-3.5 ${trendColor}`} />
                  </div>
                </div>

                {/* Patient info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-slate-900">{p.patient?.name || 'Patient'}</span>
                    {p.isSilent && (
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-[10px] font-bold border border-orange-200">
                        Silent {p.hoursSilent}h
                      </span>
                    )}
                    {p.inSevenDayWindow && (
                      <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded-full text-[10px] font-bold border border-red-100">
                        Day {p.daysSinceDischarge} post-discharge
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {p.conditions.slice(0, 3).map((c, ci) => (
                      <span key={ci} className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs">{c}</span>
                    ))}
                  </div>
                </div>

                {/* Risk bar */}
                <div className="shrink-0 w-20 hidden sm:block">
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${style.bar}`} style={{ width: `${Math.min(p.riskScore, 100)}%` }} />
                  </div>
                </div>

                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PopulationView;
